import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { DatePicker } from '@components/core-ui/form-elements/DatePicker';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useMutation } from '@tanstack/react-query';
import { ApiKeysService } from 'app/API';
import { useState } from 'react';

import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { getCreateApiKeyErrorMessage } from './helpers/getCreateApiKeyErrorMessage';
import { Content } from './styles/Content';

type TProps = {
  onSuccess: () => void;
  onCancel: () => void;
};

export const CreateAPIKeyModal = ({ onSuccess, onCancel }: TProps) => {
  const { call } = useAPI();

  const { organization } = useAuthContext();
  const organizationId = organization?.id ?? '';

  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const createKeyMutation = useMutation({
    mutationFn: () =>
      call(() =>
        ApiKeysService.create({
          organizationId,
          requestBody: { expirationDate: expirationDate ? expirationDate.toString() : undefined },
        }),
      ),
    onSuccess: () => onSuccess(),
    onError: (error) => setErrorMessage(getCreateApiKeyErrorMessage(error)),
  });

  const submit = () => {
    if (organizationId === '') {
      return;
    }
    setErrorMessage('');
    createKeyMutation.mutate();
  };

  return (
    <ModalWrapper width="350px" overflow="visible">
      {createKeyMutation.isPending ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Create New API Key</ModalTitle>

      <Content>
        <div className="workspaceSettings-inputWrapper">
          <div className="inputContainer">
            <DatePicker
              label="Expiration Date (optional)"
              minDate={new Date()}
              maxDate={new Date(new Date().getFullYear() + 3, new Date().getMonth(), new Date().getDate())}
              value={expirationDate}
              onChange={(date: Date | null) => setExpirationDate(date ?? null)}
            />
          </div>
        </div>
        {errorMessage ? <p className="workspaceSettings-errorMessage">{errorMessage}</p> : null}
        <div className="buttonContainer">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>

          <Button disabled={createKeyMutation.isPending} onClick={submit}>
            Create
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
